import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import { CloudUpload, AdminPanelSettings } from '@mui/icons-material';
import { BRAND_COLORS } from '../../../styles/theme';
import { useS3ConsistencyAudit } from '../hooks/useS3ConsistencyAudit';

/**
 * S3 Consistency Audit admin tool. Finds orphaned DB records whose S3 files are
 * missing and optionally deletes them. Consumes useS3ConsistencyAudit internally.
 */
export const S3AuditCard: React.FC = () => {
  const s3Audit = useS3ConsistencyAudit();

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <CloudUpload sx={{ mr: 1, color: BRAND_COLORS.forestGreen.main }} />
          <Typography variant="h6" component="h2">
            S3 Consistency Audit
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" paragraph>
          Find orphaned DB records where S3 files are missing.
        </Typography>

        {s3Audit.auditResult && (
          <Alert severity={s3Audit.hasOrphanedRecords ? 'error' : 'success'} sx={{ mb: 2 }}>
            <Typography variant="body2">
              Checked {s3Audit.auditResult.total_checked} records:{' '}
              {s3Audit.auditResult.valid_records} valid, {s3Audit.orphanedCount} orphaned
            </Typography>
          </Alert>
        )}

        {s3Audit.auditResult?.orphaned_records &&
          s3Audit.auditResult.orphaned_records.length > 0 && (
            <Box sx={{ mb: 2, maxHeight: 200, overflow: 'auto' }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Orphaned Records:
              </Typography>
              {s3Audit.auditResult.orphaned_records.map((record) => (
                <Typography
                  key={record.flight_id}
                  variant="body2"
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    color: 'text.secondary',
                  }}
                >
                  {/*
                    The KEY, deliberately, and the one display in the product
                    that should show one: this card lists rows whose S3 object is
                    missing, so the operator's next step is to go look for that
                    object in the bucket. `.split('/').pop()` is a no-op on a
                    minted uuid (#39 Phase C), which is the honest outcome here —
                    a uuid is what they will type into the console. Labelled
                    `key:` so it does not read as a filename; the file's NAME
                    lives on the registry row, which by definition an orphaned
                    record has nothing to say about.
                  */}
                  {/* eslint-disable-next-line no-restricted-syntax -- the one display that should show a KEY: an orphaned record has no registry row to take a name from, and the operator's next step is to find that object in the bucket. Labelled `key:`, and a no-op on a minted uuid. See the comment above. */}
                  ID {record.flight_id}: key {record.s3_key.split('/').pop()}
                </Typography>
              ))}
            </Box>
          )}

        <Chip
          icon={<AdminPanelSettings />}
          label="XER Admin Only"
          color="primary"
          variant="outlined"
          sx={{ mt: 1 }}
        />
      </CardContent>

      <CardActions sx={{ p: 2, pt: 0, gap: 1 }}>
        <Button
          variant="outlined"
          onClick={s3Audit.runAudit}
          disabled={s3Audit.auditLoading || s3Audit.deleteLoading}
          sx={{ flex: 1 }}
        >
          {s3Audit.auditLoading ? <CircularProgress size={20} /> : 'Run Audit'}
        </Button>
        {s3Audit.hasOrphanedRecords && (
          <Button
            variant="contained"
            color="error"
            onClick={s3Audit.deleteOrphanedRecords}
            disabled={s3Audit.deleteLoading || s3Audit.auditLoading}
            sx={{ flex: 1 }}
          >
            {s3Audit.deleteLoading ? (
              <CircularProgress size={20} />
            ) : (
              `Delete ${s3Audit.orphanedCount}`
            )}
          </Button>
        )}
      </CardActions>
    </Card>
  );
};
